import { APP_CONFIG, iife, logger, ROUTES } from '@sd/shared';
import { createPath } from 'history';
import { Component, ReactElement, ReactNode } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { DevMenu } from '../DevMenu/DevMenu';
import maintenancePng from './maintenance.png';
import manheimLogoPng from './manheimLogo.png';

/**
 * The error boundary is rendered outside of the router so we must use regular
 * links in this page.
 */

const Link = () => {
  'DO NOT USE LINK IN THE ERROR BOUNDARY BECAUSE ROUTING DOES NOT WORK HERE';
};
const useHistory = () => {
  'DO NOT THIS HOOK IN THE ERROR BOUNDARY BECAUSE ROUTING DOES NOT WORK HERE';
};

iife(() => [Link, useHistory]);

type ErrorInfo = {
  componentStack?: string;
};

type Props = {
  children: ReactNode;
  fallbackComponent?: (props: { errorId: string; error: unknown }) => ReactElement;
};

type State = {
  error?: Error;
  errorId?: string;
  errorInfo?: ErrorInfo;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = {};

  static getDerivedStateFromError(error: unknown) {
    // Update state so the next render will show the fallback UI.
    const errorId = v4().split('-').slice(-1)[0];
    return { error, errorId };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    logger.error('app:error', { error, errorId: this.state.errorId, errorInfo: this.state.errorInfo });
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.errorId) {
      const Fallback = this.props.fallbackComponent || DefaultFallback;
      return <Fallback error={this.state.error} errorId={this.state.errorId} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}
const DefaultFallback = (props: { error: Error | undefined; errorId: string; errorInfo?: ErrorInfo }) => (
  <>
    <DevMenu style={{ position: 'absolute', right: 0 }} />
    <Container>
      <Row>
        <Col xs={4} md={2}>
          <div>
            <Thumbnail src={manheimLogoPng}></Thumbnail>
          </div>
        </Col>
        <BlueBar xs={8} md={10} />
      </Row>
      <Row className="mt-4">
        <Col xs={12} md={6}>
          <Header>Well this was unexpected</Header>
          <div>
            <LiftImg src={maintenancePng}></LiftImg>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div>
            <h3>Our application is having trouble with this request.</h3>
            <p className="mb-3">We have alerted the repair crew.</p>
            <p className="mb-3">
              <strong>Don't panic; we don't charge for repairs.</strong>
            </p>
            <p>In the meantime you can try to:</p>
            <ul>
              <li className="mb-2">
                {/* Cannot use <Link> here because routing will not work in the error boundary */}
                <a href={createPath(ROUTES.appShell.logout.locationFor())}>Logout</a> and log back into the application.
              </li>
              <li className="mb-2">
                Try{' '}
                <a href={window.location.href} onClick={() => window.location.reload()}>
                  reloading.
                </a>
              </li>
              <li className="mb-2">Clear your browser history.</li>
            </ul>
            <h3>Still experiencing a problem?</h3>
            <p>
              <a href="mailto:info@manheim.com">Email</a> or <a href="tel:1-866-626-4346">call</a> Manheim Customer Care
              at <a href="tel:1-866-626-4346">1-866-MANHEIM (626-4346)</a>, Mon-Sat 8a-12m and Sun 6p-10p ET
            </p>
            <p>
              Any information you can provide as to what you were doing when this happened would be GREAT (including
              screenshots and the reference code below)!
            </p>
            <h5 className="mt-3">Error Code Reference: {props.errorId}</h5>
          </div>
        </Col>
      </Row>
    </Container>
    {APP_CONFIG.build.environment !== 'prod' && (
      <ErrorDetails error={props.error} errorId={props.errorId} errorInfo={props.errorInfo} />
    )}
  </>
);

const ErrorDetails = (props: { error?: Error; errorId: string; errorInfo?: ErrorInfo }) => {
  // eslint-disable-next-line no-console
  console.error('Developer Error Details', props);
  return (
    <ErrorDetailsContainer>
      <h4>Developer Details</h4>
      <small>ErrorId: {props.errorId}</small>
      <p>You may find more stack information by looking at the developer console</p>
      <h5>Stack</h5>
      <pre>{props.error?.stack || props.error?.message}</pre>
      {props.errorInfo?.componentStack && (
        <>
          <hr />
          <h5>Component Stack</h5>
          <pre>{props.errorInfo.componentStack}</pre>
        </>
      )}
    </ErrorDetailsContainer>
  );
};

const ErrorDetailsContainer = styled.div`
  margin: ${({ theme }) => theme.spacing.spacer3};
  padding: ${({ theme }) => theme.spacing.spacer3};
  border: 1px solid ${({ theme }) => theme.colors.secondaryDark};
`;
const Thumbnail = styled.img`
  max-width: 100%;
  height: auto;
`;
const LiftImg = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;
const Header = styled.h2`
  text-align: center;
`;
const BlueBar = styled(Col)`
  background-color: ${({ theme }) => theme.colors.secondaryDark};
`;
